import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import env from '@jwp/ott-common/src/env';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';
import { GET_ALL_MEDIA_APPROVALS_LIST, GetAllMediaApprovalsListData, GetAllMediaApprovalsListVariables, MediaApprovalMaStatus } from '@jwp/ott-common/src/graphql/query/moderate.queries';
import { formatDateDDMMYYYY } from '@jwp/ott-common/src/utils/datetime';
import BackwardArrow from '@jwp/ott-theme/assets/icons/arrow_backward.svg';
import ChevronRight from '@jwp/ott-theme/assets/icons/chevron_right.svg?react';
import { createURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { PATH_HOME } from '@jwp/ott-common/src/paths';

import IconButton from '../IconButton/IconButton';
import Icon from '../Icon/Icon';
import Skeleton from '../Skeleton/Skeleton';
import Button from '../Button/Button';
import { formatDurationWithLabelsMs } from '../../utils/formatTime';

import styles from './ModerateContent.module.scss';

const ModerateContent = () => {
  const { t } = useTranslation(['user', 'video', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAccountStore((state) => state.user);
  const [selectedStatus, setSelectedStatus] = React.useState<MediaApprovalMaStatus | null>(null);
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const { data, loading, error, fetchMore } = useQuery<GetAllMediaApprovalsListData, GetAllMediaApprovalsListVariables>(GET_ALL_MEDIA_APPROVALS_LIST, {
    variables: {
      first: 10,
      offset: 0,
      maType: ['REELS', 'VIDEOS'],
      maStatus: selectedStatus ? [selectedStatus] : undefined,
    },
    fetchPolicy: 'cache-and-network',
  });

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  React.useEffect(() => {
    if (!loading && user?.role !== 'superadmin') {
      navigate(PATH_HOME, { replace: true });
    }
  }, [user, loading, navigate]);

  const handleBackClick = () => {
    navigate(PATH_HOME);
  };

  const handleReview = (id: string, contentType: string) => {
    useUploadStore.getState().setInitiated(true);
    if (contentType === 'Video') {
      navigate(createURLFromLocation(location, { u: 'video-review', id }));
    } else {
      navigate(createURLFromLocation(location, { u: 'reel-review', id }));
    }
  };

  const handleViewDetails = (id: string, contentType: string) => {
    useUploadStore.getState().setInitiated(true);
    if (contentType === 'Video') {
      navigate(createURLFromLocation(location, { u: 'video-details', id }));
    } else {
      navigate(createURLFromLocation(location, { u: 'reel-details', id }));
    }
  };

  const handleLoadMore = () => {
    if (!data) return;

    fetchMore({
      variables: {
        offset: data.getAllMediaApprovalsList.nodes.length,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          getAllMediaApprovalsList: {
            ...fetchMoreResult.getAllMediaApprovalsList,
            nodes: [...prev.getAllMediaApprovalsList.nodes, ...fetchMoreResult.getAllMediaApprovalsList.nodes],
          },
        };
      },
    });
  };

  const nodes = data?.getAllMediaApprovalsList?.nodes || [];
  const totalCount = data?.getAllMediaApprovalsList?.totalCount || 0;
  const hasMore = nodes.length < totalCount;

  const renderRows = () => {
    if (loading && nodes.length === 0) {
      return ['sk-1', 'sk-2', 'sk-3', 'sk-4', 'sk-5'].map((key) => (
        <tr key={key}>
          <td>
            <div className={styles.thumbnailCell}>
              <Skeleton width="30px" height="52px" borderRadius="4px" />
              <Skeleton width="120px" height="16px" />
            </div>
          </td>
          <td>
            <Skeleton width="100px" height="16px" />
          </td>
          <td>
            <Skeleton width="60px" height="16px" />
          </td>
          <td>
            <Skeleton width="50px" height="16px" />
          </td>
          <td>
            <Skeleton width="80px" height="16px" />
          </td>
          <td>
            <Skeleton width="70px" height="16px" />
          </td>
          <td>
            <Skeleton width="80px" height="32px" borderRadius="16px" />
          </td>
        </tr>
      ));
    }

    if (nodes.length === 0) {
      return (
        <tr>
          <td colSpan={7} className={styles.emptyCell}>
            {t('video:no_content')}
          </td>
        </tr>
      );
    }

    const rows = nodes.map((node) => {
      const contentTypeMap: Record<string, string> = {
        reels: 'Reel',
        videos: 'Video',
      };
      const contentType = contentTypeMap[node?.maType?.toLocaleLowerCase()] || 'Video';

      return (
        <tr key={node?.maId}>
          <td>
            <div className={styles.thumbnailCell}>
              <img
                src={env.APP_BASE_URL + node?.maS3ThumbnailKeyUrl}
                alt={node?.maTitle}
                className={classNames(styles.thumb, styles[node?.maType?.toLocaleLowerCase()])}
              />
              <span className={styles.title}>{node?.maTitle ? node?.maTitle : '-'}</span>
            </div>
          </td>
          <td>
            <span className={styles.creatorName}>{node?.userByMaUploadedBy?.uFullName || '-'}</span>
          </td>
          <td>
            <span className={styles.contentType}>{contentType}</span>
          </td>
          <td>
            <span className={styles.duration}>{formatDurationWithLabelsMs(node?.maDuration)}</span>
          </td>
          <td>
            <span className={styles.date}>{formatDateDDMMYYYY(node?.maCreatedAt)}</span>
          </td>
          <td>
            <span className={styles.statusText}>{node?.maStatus ? node?.maStatus?.toLocaleLowerCase() : '-'}</span>
          </td>
          <td>
            {node?.maStatus?.toLocaleLowerCase() === 'pending' ? (
              <Button label="Review" color="primary" variant="contained" onClick={() => handleReview(node.maId, contentType)} />
            ) : (
              <Button label="Reviewed" variant="transparent" onClick={() => handleViewDetails(node.maId, contentType)} />
            )}
          </td>
        </tr>
      );
    });

    if (hasMore) {
      rows.push(
        <tr key="load-more">
          <td colSpan={7} className={styles.loadMore}>
            <Button label={t('common:load_more')} variant="outlined" onClick={handleLoadMore} busy={loading} />
          </td>
        </tr>
      );
    }

    return rows;
  };

  if (error) {
    return <div className={styles.container}>{t('common:error_loading_data')}</div>;
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerTitlePart}>
          <IconButton className={styles.iconButton} onClick={handleBackClick}>
            <img src={BackwardArrow} alt="Back" />
          </IconButton>
          <h1>{t('user:nav.moderate')}</h1>
        </div>
        <div className={styles.filterWrapper} ref={dropdownRef}>
          <button className={`${styles.filterBtn} ${isFilterOpen ? styles.open : ''}`} onClick={() => setIsFilterOpen(!isFilterOpen)}>
            <span>{selectedStatus ? selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1).toLowerCase() : 'Filter'}</span>
            <Icon icon={ChevronRight} className={styles.icon} />
          </button>
          {isFilterOpen && (
            <div className={styles.filterDropdown}>
              <button
                className={`${styles.filterItem} ${selectedStatus === null ? styles.selected : ''}`}
                onClick={() => {
                  setSelectedStatus(null);
                  setIsFilterOpen(false);
                }}
              >
                All
              </button>
              <div className={styles.filterDivider} />
              <button
                className={`${styles.filterItem} ${selectedStatus === 'PENDING' ? styles.selected : ''}`}
                onClick={() => {
                  setSelectedStatus('PENDING');
                  setIsFilterOpen(false);
                }}
              >
                Pending
              </button>
              <div className={styles.filterDivider} />
              <button
                className={`${styles.filterItem} ${selectedStatus === 'APPROVED' ? styles.selected : ''}`}
                onClick={() => {
                  setSelectedStatus('APPROVED');
                  setIsFilterOpen(false);
                }}
              >
                Approved
              </button>
              <div className={styles.filterDivider} />
              <button
                className={`${styles.filterItem} ${selectedStatus === 'REJECTED' ? styles.selected : ''}`}
                onClick={() => {
                  setSelectedStatus('REJECTED');
                  setIsFilterOpen(false);
                }}
              >
                Rejected
              </button>
            </div>
          )}
        </div>
      </header>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Thumbnail</th>
              <th>Creator</th>
              <th>Content Type</th>
              <th>Duration</th>
              <th>Uploaded Date</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>{renderRows()}</tbody>
        </table>
      </div>
    </div>
  );
};

export default ModerateContent;
